<template>
  <figure class="product-frame" :class="`product-frame--${mode}`">
    <div class="product-frame__bar">
      <span class="product-frame__dots" aria-hidden="true"><i /><i /><i /></span>
      <span class="product-frame__label">{{ label }}</span>
      <span class="product-frame__brand">Workspace369</span>
    </div>

    <div v-if="image" class="product-frame__image-wrap">
      <img
        :src="image"
        :alt="imageAlt || title"
        :loading="priority ? 'eager' : 'lazy'"
        :fetchpriority="priority ? 'high' : 'auto'"
        width="1200"
        height="760"
      />
    </div>

    <div v-else class="product-frame__preview" :aria-label="`${title} interface preview`" role="img">
      <template v-if="mode === 'workflow'">
        <div class="preview-toolbar">
          <span>Workflow path</span>
          <b>Ready to test</b>
        </div>
        <div class="workflow-path">
          <div class="workflow-node">
            <i class="workflow-node__icon">1</i>
            <span><small>Trigger</small><strong>Client activity</strong></span>
            <em class="fp-pill fp-pill--ready">Automated</em>
          </div>
          <span class="workflow-connector" aria-hidden="true" />
          <div class="workflow-node">
            <i class="workflow-node__icon">2</i>
            <span><small>Condition</small><strong>Match the right record</strong></span>
            <em class="fp-pill fp-pill--ready">Checked</em>
          </div>
          <span class="workflow-connector" aria-hidden="true" />
          <div class="workflow-node workflow-node--accent">
            <i class="workflow-node__icon">3</i>
            <span><small>Action</small><strong>Continue the workflow</strong></span>
            <em class="fp-pill fp-pill--next">Next</em>
          </div>
        </div>
      </template>

      <template v-else-if="mode === 'log'">
        <div class="preview-toolbar">
          <span>Workflow runs</span>
          <b>Executions&nbsp;&nbsp; Test runs</b>
        </div>
        <div class="run-log">
          <div v-for="(status, index) in ['Completed', 'Completed', 'Test run']" :key="status + index" class="run-log__row">
            <i :class="{ 'is-test': index === 2 }" />
            <span>
              <strong>{{ index === 2 ? 'Simulated workflow' : 'Client follow-up' }}</strong>
              <small>{{ index === 2 ? 'No message sent' : 'Record and recipient saved' }}</small>
            </span>
            <em class="fp-pill" :class="index === 2 ? 'fp-pill--test' : 'fp-pill--done'">{{ status }}</em>
          </div>
        </div>
        <div class="run-log__summary">
          <span><small>Status</small><strong>Completed</strong></span>
          <span><small>History</small><strong>Recorded</strong></span>
          <span><small>Review</small><strong>Ready</strong></span>
        </div>
      </template>

      <template v-else-if="mode === 'timeline'">
        <div class="preview-toolbar">
          <span>Activity timeline</span>
          <b>All activity</b>
        </div>
        <div class="activity-timeline">
          <div v-for="(item, index) in ['Record updated', 'Team follow-up', 'Client notified']" :key="item">
            <i>{{ index + 1 }}</i>
            <span><strong>{{ item }}</strong><small>Context stays attached to the work.</small></span>
            <em>{{ index === 0 ? 'Now' : `${index + 2}m` }}</em>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="preview-toolbar">
          <span>Workspace view</span>
          <b>Live context</b>
        </div>
        <div class="record-layout">
          <aside aria-hidden="true">
            <i v-for="index in 5" :key="index" :class="{ active: index === 2 }" />
          </aside>
          <section>
            <div class="record-layout__heading"><i /><span /></div>
            <div class="record-layout__metrics">
              <i v-for="index in 3" :key="index" />
            </div>
            <div class="record-layout__table">
              <span v-for="index in 4" :key="index"><i /><b /></span>
            </div>
          </section>
        </div>
      </template>
    </div>

    <figcaption v-if="caption" class="product-frame__caption">
      <strong>{{ title }}</strong>
      <span>{{ caption }}</span>
    </figcaption>
  </figure>
</template>

<script setup>
defineProps({
  title: { type: String, required: true },
  caption: { type: String, default: "" },
  label: { type: String, default: "Product view" },
  image: { type: String, default: "" },
  imageAlt: { type: String, default: "" },
  mode: { type: String, default: "records" },
  priority: { type: Boolean, default: false },
});
</script>

<style scoped>
.product-frame {
  overflow: hidden;
  margin: 0;
  border: 1px solid #e2e8f0;
  border-radius: 0.9rem;
  background: #ffffff;
  box-shadow: 0 28px 70px rgb(15 23 42 / 0.1);
}

.product-frame__bar {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 0.75rem;
  align-items: center;
  min-height: 3rem;
  border-bottom: 1px solid #e8edf2;
  background: #fafbfc;
  padding: 0.7rem 1rem;
}

.product-frame__dots {
  display: flex;
  gap: 0.38rem;
}

.product-frame__dots i {
  width: 0.52rem;
  height: 0.52rem;
  border-radius: 999px;
  background: #c3ccd6;
}

.product-frame__label,
.product-frame__brand {
  color: #334155;
  font-size: 0.76rem;
  font-style: normal;
  font-weight: 850;
  line-height: 1.2;
}

.product-frame__brand {
  color: #64748b;
  font-size: 0.68rem;
  text-transform: uppercase;
}

.product-frame__image-wrap {
  overflow: hidden;
  background: #f5f4f2;
}

.product-frame__image-wrap img {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 10;
  object-fit: contain;
  object-position: center;
}

.product-frame__preview {
  min-height: clamp(19rem, 43vw, 30rem);
  background: #f5f4f2;
  padding: clamp(1.1rem, 3vw, 2rem);
}

.preview-toolbar {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  border-bottom: 1px solid #e8e6e3;
  padding-bottom: 0.9rem;
}

.preview-toolbar span {
  color: #1c1917;
  font-size: 0.9rem;
  font-weight: 900;
}

.preview-toolbar b {
  color: #0e7490;
  font-size: 0.68rem;
  font-weight: 900;
  text-transform: uppercase;
}

/* App-style status pills */
.fp-pill {
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 0.3rem;
  border-radius: 999px;
  padding: 0.24rem 0.62rem;
  font-size: 0.62rem;
  font-style: normal;
  font-weight: 900;
  text-transform: uppercase;
}

.fp-pill::before {
  width: 0.34rem;
  height: 0.34rem;
  border-radius: 999px;
  content: "";
}

.fp-pill--ready,
.fp-pill--done {
  background: #e6f7ed;
  color: #15803d;
}

.fp-pill--ready::before,
.fp-pill--done::before {
  background: #16a34a;
}

.fp-pill--test,
.fp-pill--next {
  background: #f1edfe;
  color: #6d28d9;
}

.fp-pill--test::before,
.fp-pill--next::before {
  background: #7c3aed;
}

.workflow-path {
  display: grid;
  max-width: 34rem;
  margin: clamp(1.3rem, 4vw, 3rem) auto 0;
}

.workflow-node {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 0.9rem;
  align-items: center;
  border: 1px solid #e8e6e3;
  border-radius: 0.8rem;
  background: #ffffff;
  padding: 1rem;
  box-shadow: 0 10px 24px rgb(28 25 23 / 0.05);
}

.workflow-node--accent {
  border-color: rgb(124 58 237 / 0.35);
}

.workflow-node__icon {
  display: grid;
  width: 2rem;
  height: 2rem;
  place-items: center;
  border-radius: 0.4rem;
  background: #e4f7fc;
  color: #087ca0;
  font-size: 0.76rem;
  font-style: normal;
  font-weight: 950;
}

.workflow-node span {
  display: grid;
  gap: 0.15rem;
}

.workflow-node small,
.run-log small,
.run-log__summary small,
.activity-timeline small,
.activity-timeline em {
  color: #57534e;
  font-size: 0.67rem;
  font-style: normal;
  font-weight: 750;
}

.workflow-node strong,
.run-log strong,
.activity-timeline strong {
  color: #1c1917;
  font-size: 0.85rem;
  font-weight: 900;
}

.workflow-connector {
  width: 1px;
  height: 1.2rem;
  margin-left: 2rem;
  background: #ddd9d4;
}

.run-log {
  overflow: hidden;
  margin-top: 1.4rem;
  border: 1px solid #e8e6e3;
  border-radius: 0.8rem;
  background: #ffffff;
}

.run-log__row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 0.8rem;
  align-items: center;
  border-bottom: 1px solid #efedea;
  padding: 0.95rem;
}

.run-log__row:last-child {
  border-bottom: 0;
}

.run-log__row > i {
  width: 0.58rem;
  height: 0.58rem;
  border-radius: 999px;
  background: #16a34a;
}

.run-log__row > i.is-test {
  background: #7c3aed;
}

.run-log__row span,
.activity-timeline span {
  display: grid;
  gap: 0.18rem;
}

.run-log__summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.65rem;
  margin-top: 0.9rem;
}

.run-log__summary span {
  display: grid;
  gap: 0.25rem;
  border-top: 2px solid #1ba7ce;
  background: #ffffff;
  padding: 0.75rem;
}

.run-log__summary strong {
  color: #1c1917;
  font-size: 1rem;
}

.activity-timeline {
  display: grid;
  max-width: 34rem;
  margin: 1.4rem auto 0;
}

.activity-timeline > div {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 0.9rem;
  align-items: center;
  border-left: 1px solid #ddd9d4;
  padding: 0.7rem 0 0.7rem 1rem;
}

.activity-timeline > div > i {
  display: grid;
  width: 1.7rem;
  height: 1.7rem;
  place-items: center;
  border-radius: 999px;
  background: #1c1917;
  color: #ffffff;
  font-size: 0.68rem;
  font-style: normal;
  font-weight: 900;
}

.record-layout {
  display: grid;
  grid-template-columns: 3.5rem minmax(0, 1fr);
  overflow: hidden;
  min-height: 20rem;
  margin-top: 1.4rem;
  border: 1px solid #e8e6e3;
  border-radius: 0.8rem;
  background: #ffffff;
}

.record-layout aside {
  display: grid;
  align-content: start;
  gap: 0.7rem;
  border-right: 1px solid #efedea;
  background: #faf9f7;
  padding: 1rem;
}

.record-layout aside i {
  height: 1.3rem;
  border-radius: 0.22rem;
  background: #d6d3d1;
}

.record-layout aside i.active {
  background: #1ba7ce;
}

.record-layout section {
  padding: 1.2rem;
}

.record-layout__heading {
  display: flex;
  gap: 0.7rem;
  align-items: center;
}

.record-layout__heading i {
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 0.35rem;
  background: #dff6fc;
}

.record-layout__heading span {
  width: min(14rem, 55%);
  height: 0.8rem;
  border-radius: 0.2rem;
  background: #d6d3d1;
}

.record-layout__metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.65rem;
  margin-top: 1.2rem;
}

.record-layout__metrics i {
  height: 4.5rem;
  border: 1px solid #efedea;
  border-radius: 0.35rem;
  background: #faf9f7;
}

.record-layout__table {
  display: grid;
  margin-top: 1rem;
  border-top: 1px solid #efedea;
}

.record-layout__table span {
  display: grid;
  grid-template-columns: 2rem minmax(0, 1fr);
  gap: 0.7rem;
  align-items: center;
  border-bottom: 1px solid #efedea;
  padding: 0.7rem 0;
}

.record-layout__table i {
  height: 1.35rem;
  border-radius: 0.25rem;
  background: #e4f7fc;
}

.record-layout__table b {
  width: min(18rem, 72%);
  height: 0.55rem;
  border-radius: 0.2rem;
  background: #d6d3d1;
}

.product-frame__caption {
  display: grid;
  gap: 0.3rem;
  border-top: 1px solid #e8edf2;
  padding: 0.9rem 1rem 1rem;
}

.product-frame__caption strong {
  color: #0f172a;
  font-size: 0.86rem;
  font-weight: 900;
}

.product-frame__caption span {
  color: #64748b;
  font-size: 0.76rem;
  line-height: 1.5;
}

.dark .product-frame {
  border-color: #26364c;
  background: #0b1425;
  box-shadow: 0 28px 70px rgb(0 0 0 / 0.28);
}

.dark .product-frame__bar,
.dark .product-frame__caption {
  border-color: #26364c;
  background: #0b1425;
}

.dark .product-frame__label,
.dark .product-frame__caption strong {
  color: #e8eef8;
}

.dark .product-frame__brand,
.dark .product-frame__caption span {
  color: #8fa0b8;
}

.dark .product-frame__preview {
  background: #0a1120;
}

.dark .product-frame__image-wrap {
  background: #0a1120;
}

.dark .preview-toolbar,
.dark .record-layout aside,
.dark .record-layout__table,
.dark .product-frame__caption {
  border-color: #26364c;
}

.dark .preview-toolbar span,
.dark .workflow-node strong,
.dark .run-log strong,
.dark .activity-timeline strong,
.dark .run-log__summary strong {
  color: #f8fafc;
}

.dark .workflow-node small,
.dark .run-log small,
.dark .run-log__summary small,
.dark .activity-timeline small,
.dark .activity-timeline em {
  color: #94a3b8;
}

.dark .workflow-node,
.dark .run-log,
.dark .run-log__summary span,
.dark .record-layout {
  border-color: #26364c;
  background: #0f172a;
}

.dark .fp-pill--ready,
.dark .fp-pill--done {
  background: #052e16;
  color: #4ade80;
}

.dark .fp-pill--test,
.dark .fp-pill--next {
  background: #2e1065;
  color: #c4b5fd;
}

.dark .record-layout aside {
  background: #0b1425;
}

.dark .record-layout section {
  background: #0f172a;
}

.dark .record-layout__metrics i {
  border-color: #26364c;
  background: #0b1425;
}

@media (max-width: 640px) {
  .product-frame__bar {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .product-frame__brand {
    display: none;
  }

  .product-frame__preview {
    min-height: 18rem;
    padding: 0.9rem;
  }

  .workflow-node {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .workflow-node .fp-pill {
    display: none;
  }

  .run-log__summary {
    grid-template-columns: 1fr;
  }

  .record-layout {
    grid-template-columns: 2.7rem minmax(0, 1fr);
  }

  .record-layout aside {
    padding: 0.75rem;
  }

  .record-layout__metrics {
    grid-template-columns: 1fr 1fr;
  }

  .record-layout__metrics i:last-child {
    display: none;
  }
}

.dark .preview-toolbar b { color: #67e8f9; }
</style>
